import React, { useState } from 'react';
import {
  Button, Card, DatePicker, Form, Input, InputNumber, Radio,
  Space, Spin, Table, Tabs, Popconfirm, Select,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, SaveOutlined, EditOutlined,
  LeftOutlined, RightOutlined, SearchOutlined, UndoOutlined,
  FileAddOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import LookupField from '../../components/LookupField';
import type { F2131Input } from '../../services/pw2203Api';
import { pw2203Api } from '../../services/pw2203Api';
import { usePW2203 } from './usePW2203';
import { BatchAcceptanceBridge, pw2203f1AcceptanceManifest } from '../../features/acceptance';
import './PW2203Form.css';

const PW2203Form: React.FC = () => {
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');

  const {
    mode, records, currentIdx, details, loading, total,
    current, isEditing, isApproved,
    loadList, handlePrev, handleNext,
    handleAdd, handleEdit, handleCancel, handleSave, handleDelete,
    addDetailRow, deleteDetailRow, updateDetail,
  } = usePW2203(form);

  // ── Detail columns ────────────────────────────────────────
  const detailColumns: ColumnsType<F2131Input> = [
    { title: '項次', key: 'seq', width: 50, render: (_, __, i) => i + 1 },
    {
      title: '產品代號', dataIndex: 'f2131_11', width: 120,
      render: (v, _, i) => isEditing
        ? <Input size="small" value={v} onChange={e => updateDetail(i, 'f2131_11', e.target.value)} />
        : v,
    },
    { title: '產品名稱', dataIndex: 'f2131_11a', width: 160, render: v => v || '' },
    { title: '單位', dataIndex: 'f2131_12', width: 55, render: v => v || '' },
    {
      title: '數量', dataIndex: 'f2131_13', width: 80,
      render: (v, _, i) => isEditing
        ? <InputNumber size="small" value={v} min={0} style={{ width: 75 }} onChange={val => updateDetail(i, 'f2131_13', val ?? 0)} />
        : v?.toLocaleString(),
    },
    {
      title: '單價', dataIndex: 'f2131_14', width: 90,
      render: (v, _, i) => isEditing
        ? <InputNumber size="small" value={v} min={0} style={{ width: 85 }} onChange={val => updateDetail(i, 'f2131_14', val ?? 0)} />
        : v?.toLocaleString(),
    },
    { title: '金額', dataIndex: 'f2131_15', width: 90, render: v => (v ?? 0).toLocaleString() },
    {
      title: '贈量', dataIndex: 'f2131_16', width: 70,
      render: (v, _, i) => isEditing
        ? <InputNumber size="small" value={v} min={0} style={{ width: 65 }} onChange={val => updateDetail(i, 'f2131_16', val ?? 0)} />
        : v?.toLocaleString() || '',
    },
    {
      title: '摘要', dataIndex: 'f2131_18', width: 140,
      render: (v, _, i) => isEditing
        ? <Input size="small" value={v} maxLength={60} onChange={e => updateDetail(i, 'f2131_18', e.target.value)} />
        : v,
    },
    {
      title: '訂單編號', dataIndex: 'f2131_21', width: 100,
      render: (v, _, i) => isEditing
        ? <Input size="small" value={v} maxLength={10} onChange={e => updateDetail(i, 'f2131_21', e.target.value)} />
        : v || '',
    },
    {
      title: '訂單序號', dataIndex: 'f2131_22', width: 70,
      render: (v, _, i) => isEditing
        ? <InputNumber size="small" value={v} min={0} style={{ width: 65 }} onChange={val => updateDetail(i, 'f2131_22', val ?? 0)} />
        : v || '',
    },
    {
      title: '', key: 'del', width: 40,
      render: (_, __, i) => isEditing
        ? <Button size="small" danger type="link" icon={<DeleteOutlined />} onClick={() => deleteDetailRow(i)} />
        : null,
    },
  ];

  // ── Calculated totals ─────────────────────────────────────
  const subtotal = details.reduce((s, d) => s + (d.f2131_15 ?? 0), 0);
  const transport = form.getFieldValue('f2130_23') ?? 0;
  const freight = form.getFieldValue('f2130_25') ?? 0;
  const preTax = subtotal + Number(transport) + Number(freight);
  const taxType = form.getFieldValue('f2130_18') ?? 1;
  const tax = taxType === 1 ? Math.round(preTax * 0.05) : 0;
  const totalInclTax = preTax + tax;
  const received = form.getFieldValue('f2130_21') ?? 0;
  const outstanding = totalInclTax - Number(received);

  const tabItems = [
    {
      key: 'page1',
      label: '銷貨出庫',
      children: (
        <div className="pw2203-tab-content">
          <div className="pw2203-field-row">
            <Form.Item label="銷貨出庫單號">
              <Input value={current?.f2130_01 || (mode === 'add' ? '*新增*' : '')} readOnly disabled />
            </Form.Item>
            <Form.Item label="跳號別" name="f2130_31">
              <Select data-contract-field="f2130_31" disabled={!isEditing} style={{ width: 120 }}>
                <Select.Option value="">日期流水</Select.Option>
                <Select.Option value="M">月別M序</Select.Option>
                <Select.Option value="P">月別P序</Select.Option>
              </Select>
            </Form.Item>
          </div>
          <Form.Item label="出庫日期" name="f2130_11" rules={[{ required: true, message: '日期不可為空白!' }]}>
            <DatePicker disabled={!isEditing} style={{ width: 200 }} />
          </Form.Item>
          <Form.Item label="庫號">
            <LookupField
              lookupType="warehouse"
              value={form.getFieldValue('f2130_07') || ''}
              onChange={(code) => form.setFieldsValue({ f2130_07: code })}
              disabled={!isEditing}
              title="搜尋庫號"
              style={{ width: 100 }} contractFieldId="f2130_07"
            />
          </Form.Item>
          <Form.Item label="銷售人員">
            <LookupField
              lookupType="employee"
              value={form.getFieldValue('f2130_12') || ''}
              onChange={(code) => form.setFieldsValue({ f2130_12: code })}
              disabled={!isEditing}
              title="搜尋銷售人員"
              style={{ width: 150 }} contractFieldId="f2130_12"
            />
          </Form.Item>
          <Form.Item label="公司代號">
            <LookupField
              lookupType="customer"
              value={form.getFieldValue('f2130_13') || ''}
              onChange={(code) => form.setFieldsValue({ f2130_13: code })}
              disabled={!isEditing}
              title="搜尋公司代號"
              style={{ width: 180 }} contractFieldId="f2130_13"
            />
          </Form.Item>
          <Form.Item label="公司名稱">
            <LookupField
              lookupType="customer"
              value={form.getFieldValue('f2130_29') || ''}
              onChange={(code) => form.setFieldsValue({ f2130_29: code })}
              disabled={!isEditing}
              title="搜尋公司名稱"
            />
          </Form.Item>
          <Form.Item label="單據說明" name="f2130_15">
            <Input disabled={!isEditing} maxLength={80} />
          </Form.Item>
        </div>
      ),
    },
    {
      key: 'page2',
      label: '明細',
      children: (
        <div className="pw2203-detail-tab">
          <div className="pw2203-detail-actions">
            <span data-contract-field="f2130_16">總項數：{details.length}</span>
            <span>明細金額：{subtotal.toLocaleString()}</span>
            {isEditing && (
              <Button icon={<PlusOutlined />} size="small" onClick={addDetailRow}>明細新增</Button>
            )}
          </div>
          <Table<F2131Input>
            dataSource={details}
            columns={detailColumns}
            rowKey={(_, i) => String(i)}
            size="small"
            pagination={false}
            scroll={{ x: 800, y: 300 }}
          />
        </div>
      ),
    },
    {
      key: 'page3',
      label: '金額/備註',
      children: (
        <div className="pw2203-tab-content">
          <Form.Item label="稅別" name="f2130_18">
            <Radio.Group disabled={!isEditing}>
              <Radio value={1}>含稅 5%</Radio>
              <Radio value={2}>免稅</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item label="明細金額小計">
            <span className="pw2203-calc">{subtotal.toLocaleString()}</span>
          </Form.Item>
          <Form.Item label="交通費" name="f2130_23">
            <InputNumber disabled={!isEditing} min={0} style={{ width: 150 }} />
          </Form.Item>
          <Form.Item label="運費" name="f2130_25">
            <InputNumber disabled={!isEditing} min={0} style={{ width: 150 }} />
          </Form.Item>
          <Form.Item label={<span data-contract-field="f2130_28">未稅總金額</span>}>
            <span className="pw2203-calc">{preTax.toLocaleString()}</span>
          </Form.Item>
          <Form.Item label="稅額">
            <span className="pw2203-calc">{tax.toLocaleString()}</span>
          </Form.Item>
          <Form.Item label={<span data-contract-field="f2130_20">含稅總金額</span>}>
            <span className="pw2203-calc pw2203-total">{totalInclTax.toLocaleString()}</span>
          </Form.Item>
          <Form.Item label="發票號碼" name="f2130_14">
            <Input disabled={!isEditing} maxLength={12} style={{ width: 180 }} />
          </Form.Item>
          <Form.Item label="已收總金額" name="f2130_21">
            <InputNumber disabled={!isEditing} min={0} style={{ width: 150 }} />
          </Form.Item>
          <Form.Item label={<span data-contract-field="f2130_20">待收總金額</span>}>
            <span className="pw2203-calc pw2203-outstanding">{outstanding.toLocaleString()}</span>
          </Form.Item>
          <Form.Item label="備註" name="f2130_30">
            <Input.TextArea disabled={!isEditing} rows={3} />
          </Form.Item>
          <Form.Item label={<span data-contract-field="f2130_16">總項數</span>}>
            <span className="pw2203-calc">{current?.f2130_16 ?? details.length}</span>
          </Form.Item>
          <Form.Item label={<span data-contract-field="f2130_28">未稅總金額(存檔值)</span>}>
            <span className="pw2203-calc">{(current?.f2130_28 ?? 0).toLocaleString()}</span>
          </Form.Item>
        </div>
      ),
    },
  ];

  return (
    <div className="pw2203-form">
      <Spin spinning={loading}>
        {/* Toolbar */}
        <Card size="small" className="pw2203-toolbar">
          <Space>
            <span>銷貨出庫單</span>
            {current && <span className="pw2203-doc-no">{current.f2130_01}</span>}
            {isApproved && <span className="pw2203-approved">【已核准】</span>}
            {!isEditing && (
              <>
                <Button icon={<FileAddOutlined />} onClick={handleAdd}>新增</Button>
                <Button icon={<EditOutlined />} onClick={handleEdit} disabled={!current || isApproved}>修改</Button>
                <Popconfirm title="確認刪除？" onConfirm={handleDelete} disabled={!current || isApproved}>
                  <Button danger disabled={!current || isApproved}>刪除</Button>
                </Popconfirm>
                <Button icon={<LeftOutlined />} onClick={handlePrev} disabled={currentIdx <= 0} />
                <Button icon={<RightOutlined />} onClick={handleNext} disabled={currentIdx >= records.length - 1} />
                <span className="pw2203-nav-info">{currentIdx + 1} / {total}</span>
              </>
            )}
            {isEditing && (
              <>
                <Button type="primary" icon={<SaveOutlined />} onClick={handleSave}>存檔</Button>
                <Button icon={<UndoOutlined />} onClick={handleCancel}>還原</Button>
              </>
            )}
            <Input.Search
              placeholder="搜尋單號/客戶" value={searchText}
              onChange={e => setSearchText(e.target.value)}
              onSearch={kw => loadList(kw || undefined)}
              style={{ width: 200 }}
              prefix={<SearchOutlined />}
            />
          </Space>
        </Card>

        {/* Main Form */}
        <Form form={form} layout="horizontal" labelCol={{ span: 6 }} wrapperCol={{ span: 16 }}>
          <Tabs defaultActiveKey="page1" items={tabItems} />
        </Form>
        <BatchAcceptanceBridge
          manifest={pw2203f1AcceptanceManifest}
          records={records}
          selectedKeys={current?.f2130_01 ? [current.f2130_01] : []}
          keyOf={(item) => item.f2130_01}
          queryExact={(key) => pw2203Api.getByID(key)}
          lastApiResponse={current}
          loading={loading}
          onAutoFill={handlePrev}
          autoFillDisabled={records.length === 0}
          screenDraft={{ master: form.getFieldsValue(), details }}
        />
      </Spin>
    </div>
  );
};

export default PW2203Form;
